import React, { useState, useEffect, useRef, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button, Card, CardBody, Container, Row, Col, Spinner } from "reactstrap";
import Api from "../../components/helpers/Api";
import SeoNoIndex from "../../components/helpers/SeoNoIndex";
import { refreshAuthenticatedUser } from "../../components/helpers/AuthenticationHelpers";
import { AuthContext } from "../../components/helpers/AuthContext";

const invitationCalloutStyle = {
  backgroundColor: "#5e72e4",
  borderColor: "#5e72e4",
  color: "#ffffff",
};

const formatDate = (value) => {
  if (!value) {
    return "";
  }

  return new Date(value).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const getCustomer = () => {
  const stored = sessionStorage.getItem("customer");
  return stored ? JSON.parse(stored) : null;
};

const AcceptInvite = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const autoAcceptAttempted = useRef(false);
  const customer = getCustomer();

  useEffect(() => {
    const loadInvite = async () => {
      try {
        const response = await Api.get(`/api/account/invite/${token}`);
        setInvite(response.data);
      } catch (e) {
        const status = e.response?.status;
        if (status === 404 || status === 410) {
          setError("This invitation is no longer valid or has expired.");
        } else {
          setError("Unable to load invitation. Please try again.");
        }
      } finally {
        setLoading(false);
      }
    };

    loadInvite();
  }, [token]);

  const handleAccept = async () => {
    setAccepting(true);

    try {
      await Api.post(`/api/account/invite/${token}/accept`);
      // Refresh session so accountId and accountRole are immediately current
      const freshCustomer = await refreshAuthenticatedUser();
      if (freshCustomer) {
        login(freshCustomer);
      }
      setAccepted(true);
    } catch (e) {
      const status = e.response?.status;
      const code = e.response?.data?.code;
      if (status === 409 && code === "SEAT_CAP_REACHED") {
        setError("This group has reached its maximum number of members, have you considered upgrading to an Organisation account?");
      } else if (status === 409) {
        setError("You are already a member of an account.");
      } else if (status === 402) {
        setError("This group subscription is inactive. Please contact the group owner.");
      } else if (status === 403) {
        setError("This invitation was sent to a different email address. Please sign in with the correct account.");
      } else if (status === 404) {
        setError("This invitation is no longer valid.");
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setAccepting(false);
    }
  };

  // Auto-accept when the user arrives back at this page after signing in via the redirect flow.
  // This prevents the UX dead-end where a user completes registration/sign-in but never
  // clicks the final "Accept Invitation" button and the invitation stays PENDING.
  useEffect(() => {
    if (
      !loading &&
      invite &&
      customer &&
      !customer.user?.accountId &&
      !accepted &&
      !error &&
      !autoAcceptAttempted.current
    ) {
      autoAcceptAttempted.current = true;
      handleAccept();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, invite]);

  const renderContent = () => {
    if (loading) {
      return (
        <div className="text-center py-5">
          <Spinner color="primary" />
        </div>
      );
    }

    if (error) {
      return (
        <>
          <h2 style={{ fontSize: "1rem" }} className="text-primary text-uppercase">
            Invitation unavailable
          </h2>
          <p className="description mt-3">{error}</p>
          <Button color="primary" href="/">
            Return home
          </Button>
        </>
      );
    }

    if (accepted) {
      return (
        <>
          <h2 style={{ fontSize: "1rem" }} className="text-primary text-uppercase">
            You've joined {invite.accountName}!
          </h2>
          <p className="description mt-3">
            You are now a{" "}
            <span className="text-primary text-uppercase">{invite.role}</span>{" "}
            of {invite.accountName}.
          </p>
          <Button color="primary" onClick={() => navigate("/your-profile")}>
            Go to your profile
          </Button>
        </>
      );
    }

    return (
      <>
        <div className="alert mb-4" style={invitationCalloutStyle}>
          {invite.inviterName} has invited you to join {invite.accountName} as a{" "}
          <span className="text-uppercase font-weight-bold">{invite.role}</span>.
        </div>

        {!customer && (
          <>
            <h2 style={{ fontSize: "1rem" }} className="text-primary text-uppercase">
              Sign in to accept this invitation
            </h2>
            <p className="description mt-3">
              You'll need to sign in with the email address this invitation was sent to.
            </p>
            <Button
              color="primary"
              onClick={() => navigate(`/sign-in?redirect=/invite/${token}`)}
            >
              Sign in
            </Button>
            <Button
              color="secondary"
              className="ml-3"
              onClick={() => navigate(`/register/invite/${token}`)}
            >
              Create account
            </Button>
          </>
        )}

        {customer && (
          <>
            <p className="description">
              Expires: {formatDate(invite.expiresAt)}
            </p>

            {customer.user?.accountId ? (
              <div className="alert alert-info mb-0">
                You are already a member of a group. You cannot accept this invitation.
              </div>
            ) : (
              <>
                <Button
                  color="primary"
                  onClick={handleAccept}
                  disabled={accepting}
                >
                  {accepting ? "Accepting..." : "Accept Invitation"}
                </Button>
                <p className="text-muted mt-3 mb-0">
                  You will join {invite.accountName} as a {invite.role}.
                </p>
              </>
            )}
          </>
        )}
      </>
    );
  };

  return (
    <>
      <SeoNoIndex noindex={true} />

      <div className="position-relative">
        <section className="section section-lg section-shaped pb-250">
          <div className="shape shape-style-1 shape-default">
            <span />
            <span />
            <span />
            <span />
            <span />
            <span />
            <span />
            <span />
            <span />
          </div>
          <Container className="py-lg-md d-flex">
            <div className="col px-0">
              <Row>
                <Col lg="8">
                  <h1 className="display-3 text-white">
                    Join your group{" "}
                    <span>accept invitation</span>
                  </h1>
                </Col>
              </Row>
            </div>
          </Container>
          <div className="separator separator-bottom separator-skew">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              preserveAspectRatio="none"
              version="1.1"
              viewBox="0 0 2560 100"
              x="0"
              y="0"
            >
              <polygon className="fill-white" points="2560 0 2560 100 0 100" />
            </svg>
          </div>
        </section>
      </div>
      <section className="section section-lg pt-lg-0 mt--200">
        <Container>
          <Row className="justify-content-center">
            <Col lg="8">
              <Card className="card-lift--hover shadow border-0">
                <CardBody className="py-5">
                  {renderContent()}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default AcceptInvite;
